import { Download, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Product } from "@nh/shared";
import { Button } from "@/shared/ui/core";

export interface ProductDatasheetButtonProps {
  datasheet: Product["datasheet"];
}

/**
 * "Descargar ficha técnica" — el PDF del producto.
 *
 * ── No se pinta si no hay ficha ──────────────────────────────────────
 * `datasheet` es opcional en el contrato, y su `url` puede venir como
 * cadena vacía (el formulario del panel guarda "" cuando se borra el
 * campo, no `undefined`). Los dos casos son "no hay ficha": un botón de
 * descarga que no descarga nada es peor que no tener botón.
 *
 * ── Es un enlace, no un botón ────────────────────────────────────────
 * `Button` se pinta `as="a"` para que sea un ancla de verdad: abrir en
 * pestaña nueva, copiar el enlace y el menú contextual son cosas que
 * quien consulta una ficha técnica espera poder hacer, y un `<button>`
 * con `onClick` se las quita.
 *
 * `rel="noopener noreferrer"` acompaña obligatoriamente a `target=_blank`;
 * sin él la pestaña que se abre puede reescribir la nuestra.
 *
 * ── El `download` es una sugerencia, no una orden ─────────────────────
 * Solo lo obedece el navegador si el PDF vive en nuestro mismo origen; si
 * está en otro dominio, lo abre en su visor. Por eso el nombre del
 * archivo se pasa igualmente, y no se promete "se descargará".
 *
 * ── Los dos iconos ───────────────────────────────────────────────────
 * Son los del original: la flecha dice qué hace, el documento dice de qué
 * tipo es. El segundo va atenuado y `aria-hidden` — no aporta nada a
 * quien escucha la página, que ya oyó "descargar ficha técnica".
 */
export function ProductDatasheetButton({ datasheet }: ProductDatasheetButtonProps) {
  const { t } = useTranslation();

  const url = datasheet?.url;
  if (!url) return null;

  return (
    <Button
      as="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      download={datasheet.name || "ficha-tecnica.pdf"}
      variant="secondary"
      size="lg"
      fullWidth
      leftIcon={<Download />}
      rightIcon={<FileText className="opacity-50" aria-hidden="true" />}
    >
      {t("Descargar ficha técnica")}
    </Button>
  );
}
